import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlinePublicationHighlightEditor from "@/components/admin/Content/WebPages/InlineEditors/InlinePublicationHighlightEditor";
import { usePublicationHighlights } from "@/lib/api/hooks/publication";
import { Publication } from "@/lib/api/types";

const KeyResources = ({ data }: { data: WebsitePageSection }) => {
    // Fetch live highlights
    const { data: highlightsData } = usePublicationHighlights('HOME_PAGE_KEY_RESOURCES');
    const highlights = highlightsData?.data || [];

    const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-2_xbv8lj.png";

    // Format date helper
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${month} ${year}`;
    };

    // Format category helper
    const formatCategory = (category: string | null | undefined): string => {
        if (!category) return "Publication";

        return category
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <EditableSection
            sectionTag={data.sectionTag}
            contentType={data.contentType}
            data={data}
            customEditor={InlinePublicationHighlightEditor}
        >
            <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex flex-col gap-2">
                    <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
                        Key Resources ({highlights.length}/6)
                    </Paragraph>
                    <div className="flex items-center gap-6">
                        <Heading level={4}>{data.title ?? "Key Resources"}</Heading>
                    </div>
                    <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Featured publications for the home page")} />
                </div>

                {/* Live Preview */}
                {highlights.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {highlights.map((publication: Publication, index: number) => (
                            <div key={publication.publicationId} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                {/* Position Badge */}
                                <div className="inline-flex items-center gap-2">
                                    <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                                        Resource {index + 1}
                                    </span>
                                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                                        {formatCategory(publication.publicationCategory)}
                                    </span>
                                </div>

                                {/* Cover Image */}
                                <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                                    <img
                                        src={publication.coverImageUrl || defaultImage}
                                        alt={publication.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            if (e.currentTarget.src !== defaultImage) {
                                                e.currentTarget.src = defaultImage;
                                            }
                                        }}
                                    />
                                </div>

                                {/* Content */}
                                <div className="flex flex-col gap-2">
                                    <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{publication.title}</h4>
                                    {(publication.datePublished || publication.createdByName) && (
                                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                                            {publication.datePublished && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatDate(publication.datePublished)}</p>
                                            )}
                                            {publication.createdByName && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {publication.createdByName}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500 text-sm">No publications selected. Click edit to select up to 6 publications.</p>
                    </div>
                )}
            </div>
        </EditableSection>
    );
};

export default KeyResources;
